import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { UserAvatar } from '../UserAvatar';
import {
  DEFAULT_CALENDAR_WEEKS,
  MAX_LOOKBACK_WEEKS,
  visibleFutureWeekCount,
  weekSlices,
  type WeekSlice,
} from '../../lib/capacity-forecast';
import { formatCalendarWeek, formatWeekStart, shiftWeeks, startOfLocalWeek } from '../../lib/dates';
import {
  capacityRag,
  capacityRagLabel,
  resourceDisplayName,
  type CapacityRag,
  type ResourceRow,
} from '../../lib/resource-types';
import { cn } from '../../lib/utils';

const RAG_CELL: Record<CapacityRag, string> = {
  green:
    'bg-success/15 text-success-foreground dark:bg-success/20 dark:text-success',
  amber:
    'bg-warning/20 text-warning-foreground dark:bg-warning/25 dark:text-warning',
  red: 'bg-destructive/15 text-destructive dark:bg-destructive/25',
};

const RAG_SWATCH: Record<CapacityRag, string> = {
  green: 'bg-success',
  amber: 'bg-warning',
  red: 'bg-destructive',
};

export default function TeamCalendar({ rows }: { rows: ResourceRow[] }) {
  const [offset, setOffset] = useState(0);
  const today = useMemo(() => new Date(), []);
  const thisMonday = startOfLocalWeek(today);
  const windowStart = shiftWeeks(thisMonday, -offset);
  const columnCount = offset === 0 ? visibleFutureWeekCount(rows) : DEFAULT_CALENDAR_WEEKS;
  const columns = useMemo(
    () => Array.from({ length: columnCount }, (_, index) => shiftWeeks(windowStart, index)),
    [columnCount, windowStart],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={offset >= MAX_LOOKBACK_WEEKS}
          onClick={() => setOffset((value) => Math.min(MAX_LOOKBACK_WEEKS, value + 1))}
        >
          <ChevronLeft />
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => setOffset((value) => Math.max(0, value - 1))}
        >
          Next
          <ChevronRight />
        </Button>
        <ul
          className="ml-auto flex items-center gap-3 text-xs text-muted-foreground"
          aria-label="Capacity"
        >
          {(['green', 'amber', 'red'] as const).map((rag) => (
            <li key={rag} className="inline-flex items-center gap-1.5">
              <span className={cn('h-2 w-2 rounded-sm', RAG_SWATCH[rag])} aria-hidden />
              {capacityRagLabel(rag)}
            </li>
          ))}
        </ul>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-max border-collapse text-left text-sm">
          <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="sticky left-0 z-20 min-w-[12rem] bg-muted/40 px-3 py-2 font-medium">Name</th>
              {columns.map((start) => {
                const isThisWeek = start.getTime() === thisMonday.getTime();
                return (
                  <th
                    key={formatWeekStart(start)}
                    className={cn(
                      'min-w-[8.5rem] px-3 py-2 font-medium',
                      isThisWeek && 'text-foreground',
                    )}
                  >
                    {isThisWeek ? (
                      <p className="text-[10px] font-medium uppercase tracking-wide text-foreground">This week</p>
                    ) : null}
                    <p>{formatCalendarWeek(start)}</p>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <CalendarRow key={row.user_id} row={row} columns={columns} today={today} thisMonday={thisMonday} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CalendarRow({
  row,
  columns,
  today,
  thisMonday,
}: {
  row: ResourceRow;
  columns: Date[];
  today: Date;
  thisMonday: Date;
}) {
  if (!Array.isArray(row.closed_weeks)) {
    throw new Error('Team calendar requires closed_weeks on each person.');
  }
  const slices = row.weekly_capacity_hours > 0
    ? weekSlices(row.allocated_hours, row.weekly_capacity_hours, today, 12)
    : [];
  const sliceByStart = new Map<string, WeekSlice>();
  for (const slice of slices) {
    sliceByStart.set(formatWeekStart(slice.start), slice);
  }
  const closedByStart = new Map(row.closed_weeks.map((week) => [week.week_start, week.hours]));
  const name = resourceDisplayName(row);

  return (
    <tr className="border-b last:border-0">
      <td className="sticky left-0 z-10 bg-background px-3 py-2.5 align-top">
        <div className="flex items-start gap-2.5">
          <UserAvatar name={name} src={row.avatar_url} size={32} />
          <div>
            <Link to={`/resources/${row.user_id}`} className="font-medium hover:underline">
              {name}
            </Link>
            <p className="text-xs text-muted-foreground">{row.active_count} active</p>
          </div>
        </div>
      </td>
      {columns.map((start) => {
        const key = formatWeekStart(start);
        if (start.getTime() < thisMonday.getTime()) {
          const hours = closedByStart.get(key) ?? 0;
          return (
            <td key={key} className="px-3 py-2.5 align-middle text-xs tabular-nums text-muted-foreground">
              {hours > 0 ? `Closed ${formatHours(hours)}h` : '0h closed'}
            </td>
          );
        }
        const slice = sliceByStart.get(key);
        if (!slice) {
          return (
            <td key={key} className="px-3 py-2.5 align-middle text-xs text-muted-foreground">
              —
            </td>
          );
        }
        const rag = capacityRag(slice.usedHours, slice.capacityHours);
        const label = slice.freeHours === 0 ? 'Full' : `${formatHours(slice.freeHours)}h free`;
        const usedLabel = `${formatHours(slice.usedHours)}h of ${formatHours(slice.capacityHours)}h`;
        return (
          <td key={key} className="p-1 align-middle">
            <div
              className={cn(
                'rounded-md px-2.5 py-2 text-xs font-medium tabular-nums',
                RAG_CELL[rag],
              )}
              title={usedLabel}
              aria-label={`${label}, ${capacityRagLabel(rag).toLowerCase()}, ${usedLabel}`}
            >
              {label}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

function formatHours(hours: number): string {
  return String(Math.round(hours * 10) / 10);
}
